/**
 * sl-9qm.4.3: Breadcrumb Component
 *
 * Path navigation (Home > Category > Article).
 * Generated from current view context.
 */
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import { mapDatabaseRowsToBoundDataRows } from '../../../services/bound-data-row';
import type {
  ViewComponent,
  ViewRuntimeContext,
} from '../../../types/view-component.types';
import type { ViewComponentRenderProps } from '../../../types/view-component.types';
import type { ColumnInfo, Row } from '../../../types/view-datasource.types';
import { useViewNavigationState } from '../../view-renderer/view-navigation-state';
import { resolveAppBaseRuntimeHref } from '../runtime-href';
import * as styles from '../styles.css';

const configSchema = z.object({
  separator: z.string().default('/'),
  showHome: z.boolean().default(true),
  homeLabel: z.string().default('Home'),
});

export const BreadcrumbComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.BREADCRUMB,
  type: 'navigation',
  name: 'Breadcrumb',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-app-font-size-sm)' } },
    '›'
  ),
  capabilities: [],
  configSchema,
  defaultConfig: {
    separator: '/',
    showHome: true,
    homeLabel: 'Home',
  },
  render(props) {
    return createElement(BreadcrumbRenderer, props);
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      '› Breadcrumb'
    );
  },
};

function BreadcrumbRenderer({
  config,
  runtimeContext,
}: ViewComponentRenderProps) {
  const selection = useViewNavigationState()?.breadcrumb;
  const { separator, showHome, homeLabel } = configSchema.parse(config);
  const localizedHomeLabel =
    homeLabel === 'Home' &&
    runtimeContext?.locale?.toLowerCase().startsWith('de')
      ? 'Startseite'
      : homeLabel;
  const currentTitle =
    selection?.title ?? resolveBreadcrumbTitle(runtimeContext);
  const items =
    !selection?.isHome &&
    (selection || showHome) &&
    currentTitle !== localizedHomeLabel
      ? [localizedHomeLabel, currentTitle]
      : [currentTitle];

  return createElement(
    'nav',
    {
      'aria-label': 'Breadcrumb',
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sl-app-gap-sm)',
        padding: 'var(--sl-app-padding-sm) 0',
        fontSize: 'var(--sl-app-font-size-sm)',
      },
    },
    ...items.flatMap((item, i) => {
      const elements = [];
      if (i > 0) {
        elements.push(
          createElement(
            'span',
            {
              key: `sep-${i}`,
              'aria-hidden': 'true',
              style: { color: 'var(--streamlain-v2-text-disable)' },
            },
            separator
          )
        );
      }
      elements.push(
        createElement(
          i < items.length - 1 ? 'a' : 'span',
          {
            key: `item-${i}`,
            href:
              i < items.length - 1
                ? resolveAppBaseRuntimeHref(
                    selection?.homePath ?? '/',
                    runtimeContext
                  )
                : undefined,
            style: {
              color:
                i < items.length - 1
                  ? 'var(--streamlain-v2-brand-primary)'
                  : 'var(--streamlain-v2-text-primary)',
              textDecoration: 'none',
            },
            'aria-current': i === items.length - 1 ? 'page' : undefined,
          },
          item
        )
      );
      return elements;
    })
  );
}

export function resolveBreadcrumbTitle(context?: ViewRuntimeContext): string {
  const rowId =
    context?.page?.['rowId'] ??
    context?.routeParams?.['rowId'] ??
    context?.routeParams?.['id'];
  const slug = context?.routeParams?.['slug'];
  for (const value of Object.values(context?.sources ?? {})) {
    const source = value as { columns?: ColumnInfo[]; rows?: Row[] } | null;
    if (!Array.isArray(source?.columns) || !Array.isArray(source?.rows))
      continue;
    const rows = mapDatabaseRowsToBoundDataRows(source.rows, source.columns);
    const row = rows.find((candidate, index) =>
      rowId
        ? source.rows?.[index]?.id === rowId
        : slug && candidate['slug'] === slug
    );
    const title = row?.['title'] ?? row?.['name'];
    if (typeof title === 'string' && title.trim()) return title;
  }
  const title =
    context?.page?.['title'] ??
    context?.page?.['displayName'] ??
    context?.page?.['name'];
  if (title === 'Home' && context?.locale?.toLowerCase().startsWith('de')) {
    return 'Startseite';
  }
  return typeof title === 'string' && title.trim()
    ? title
    : context?.locale?.toLowerCase().startsWith('de')
      ? 'Startseite'
      : 'Home';
}
